import type { FC } from "react";

import {
  Button,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  LinkIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { DateComponent } from "@js/components/date";
import { RouterLink } from "@js/components/link";
import { Sanitize } from "@js/components/sanitize";
import type { WorkSampleItem } from "@js/types/freelancer";
import { DateFormats } from "@js/utils/date/types";

import styles from "./styles.module.scss";

type ProjectModalItemProps = {
  item: WorkSampleItem;
  index: number;
  lastIndex: number;
  openDifferentProject: (index: number) => void;
};

export const ProjectModalItem: FC<ProjectModalItemProps> = ({
  item,
  index,
  lastIndex,
  openDifferentProject,
}) => {
  return (
    <Grid container className={styles.workSampleItem}>
      <Grid size={{ xs: 12 }} sx={{ alignItems: "center", mb: 1 }}>
        <Stack direction="row" sx={{ gap: 1 }}>
          <IconButton
            disabled={index <= 0}
            onClick={() => openDifferentProject(index - 1)}
            variant="secondary"
            color="primary"
            size="small"
            aria-label="Previous project"
          >
            <ArrowLeftIcon />
          </IconButton>
          <IconButton
            onClick={() => openDifferentProject(index + 1)}
            variant="secondary"
            color="primary"
            size="small"
            aria-label="Next project"
            disabled={index >= lastIndex}
          >
            <ArrowRightIcon />
          </IconButton>
        </Stack>
      </Grid>
      <Grid
        container
        size={{ xs: 12 }}
        className={styles.workSampleItemHeader}
        sx={{ alignItems: "center" }}
      >
        <Typography
          component="span"
          variant="title"
          color="primary"
          fontWeight={500}
          sx={{ mr: "auto" }}
        >
          {item.title}
        </Typography>
      </Grid>

      <Grid
        container
        size={{ xs: 12 }}
        sx={{ alignItems: "center" }}
        className={styles.workSampleItemMeta}
      >
        {!!item.completion_date && (
          <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
            <CalendarIcon sx={{ fontSize: "18px", mb: "2px" }} />{" "}
            <DateComponent
              date={item.completion_date}
              format={DateFormats["January 1st, 1970"]}
            />
          </Stack>
        )}
        {item.url && (
          <Button
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="secondary"
            color="primary"
            RouterLink={RouterLink}
          >
            <LinkIcon /> View link
          </Button>
        )}
      </Grid>
      <Grid
        size={{
          xs: 12,
        }}
      >
        {!!item.description && (
          <Grid
            size={{
              xs: 12,
            }}
            className={styles.projectItemDescription}
          >
            <Sanitize input={item.description} />
          </Grid>
        )}
      </Grid>

      {!!item.portfolio_item_images?.length && (
        <Grid
          size={{
            xs: 12,
          }}
          className={styles.workSampleItemImages}
        >
          {item.portfolio_item_images.map((image, i) => (
            <div key={image.id} className={styles.workSampleItemImagesImage}>
              <a href={image.image.attachment.file}>
                <img
                  src={image.image.attachment.file}
                  alt={i === 0 ? "Portfolio images" : ""}
                  width="auto"
                  height="322"
                />
              </a>
            </div>
          ))}
        </Grid>
      )}
    </Grid>
  );
};
